import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { environment } from '../../../../../../../../environments/environment';
import { User } from '../../../../../../../core/interfaces/user.interface';
import { ChargesWithdrawalsRequestItem } from '../../../../../../../core/interfaces/charges-withdrawals-request.interface';
import { ChargesWithdrawalsRequestService } from '../../../../../../../core/services/charges-withdrawals-request.service';
import { DynamicModalService } from '../../../../../../../core/services/dynamic-modal.service';
import { selectAuth } from '../../../../../../../core/state/auth/auth.selector';
import { formatAmount } from '../../../../../../../core/utils/format-numeric.utils';
import { extractErrorMessage } from '../../../../../../../core/utils/extract-error-message.util';
import {
  STATE_CLASSES,
  STATE_NAMES,
  TRANSACTION_CLASSES,
  TRANSACTION_ICON_CLASSES,
  TRANSACTION_TYPE_VALUES,
  TRANSACTION_TYPES,
} from '../../../../../../../shared/data/suscriptions-payments/constants';
import { AlertService } from '../../../../../../../shared/services/alert.service';
import { EditCwRequestComponent } from '../edit-cw-request/edit-cw-request.component';
import { RepeatedVoucherActionModalComponent } from '../repeated-voucher-action-modal/repeated-voucher-action-modal.component';
import { DUPLICATE_VOUCHER_REJECT_REASON } from '../../../../../../../core/constants/constants';

interface ViewPayErrorResponse {
  error?: {
    message?: string;
    code?: string;
    data?: {
      code?: string;
    };
  };
}

interface RepeatedVoucherModalResult {
  action?: 'continue' | 'reject';
}

type ViewPayChargeWithdrawal = Omit<ChargesWithdrawalsRequestItem, 'monto'> & {
  monto?: string | number;
};

@Component({
  selector: 'app-view-pay',
  templateUrl: './view-pay.component.html',
  styleUrl: './view-pay.component.scss',
})
export class ViewPayComponent implements OnInit {
  // LOADINGS
  public isLoading: boolean = true;
  public isLoadingButton: boolean = false;

  //REDUX
  public profile$ = this.store.select(selectAuth);
  public userData!: User;

  public cwId: number = 0;
  public cwData!: ViewPayChargeWithdrawal;
  public transactionTypes = TRANSACTION_TYPES;
  public DEFAULT_VALUE: number = 0;
  public isPaymentButtonDisabled: boolean = false;

  //IMG
  public imgSrc = '';
  public urlResponse = '';
  public file = '';
  public numero_comprobante = '';
  public baseUrl: string = environment.API_FILES;

  //STATES
  public STATE_NAMES = STATE_NAMES;

  // TRANSACTION TYPES
  public TRANSACTION_TYPE_VALUES = TRANSACTION_TYPE_VALUES;

  constructor(
    private store: Store,
    private _dynamicModal: DynamicModalService,
    private _cwService: ChargesWithdrawalsRequestService,
    private activeModal: NgbActiveModal,
    private alertService: AlertService
  ) {
    this.cwId = this._dynamicModal.getModalData() as number;
  }

  ngOnInit(): void {
    this.getUserInfo();
    this.getCwInfo();
    this.getImgCw();
  }

  getCwInfo() {
    this.isLoading = true;
    this._cwService.getCw(this.cwId).subscribe((res) => {
      this.cwData = res.data;
      this.cwData.monto = this.formatAmount(
        Number(this.cwData.monto ?? 0),
        Number(this.cwData.id_pais ?? 0)
      );
      this.isLoading = false;
    });
  }

  getUserInfo() {
    this.profile$.subscribe((profile) => {
      if (profile.idusuario) {
        this.userData = profile;
      }
    });
  }

  getImgCw() {
    this.isLoading = true;
    this._cwService.getCw(this.cwId).subscribe((response) => {
      this.numero_comprobante = response.data.comprobante ?? '';
      this.urlResponse = response.data.foto_comprobante ?? '';
      this.urlResponse = this.urlResponse.replace(/ /g, '%20');
      this.file = this.urlResponse.replace(/\\/g, '/');
      this.imgSrc = this.baseUrl + '/' + this.file;
      this.isLoading = false;
    });
  }

  formatAmount(monto: number, idPais: number): string {
    return this.formatAmountWithCountry(monto, idPais);
  }

  formatAmountWithCountry(amount: number, countryId: number): string {
    return formatAmount(Number(amount), countryId === 1 ? 'EC' : 'PE');
  }

  //STATE
  getStateClasses(state: number) {
    return STATE_CLASSES[state] || { text: '', classes: {} };
  }

  //TRANSACTION TYPE
  getTransactionIconClass(type: number): string {
    return TRANSACTION_ICON_CLASSES[type] || '';
  }

  getTransactionClass(type: number): string {
    return TRANSACTION_CLASSES[type] || '';
  }

  closeModal() {
    this.activeModal.close();
  }

  // ACTIONS
  setState(id: number, newState: number, idusuarioSet: number) {
    // Utiliza la alerta de confirmación
    this.alertService
      .showConfirmAlert(
        '¿Estás seguro?',
        'No podrás revertir esto!',
        'Sí, cambiar estado!',
        'Cancelar'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.updateState(id, newState, idusuarioSet);
        }
      });
  }

  updateState(id: number, newState: number, idusuarioSet: number) {
    this._cwService.setState(id, newState, idusuarioSet).subscribe(
      () => {
        this.alertService.showSuccessAlert(
          'Se cambió el estado!',
          'La solicitud cambió su estado.'
        );
        this.closeModal();
      },
      (error: ViewPayErrorResponse) => {
        if (
          this.handleRepeatedVoucherConfirmation(
            error,
            { id, newState, idusuarioSet },
            () => this.updateStateWithRepeatedVoucher(id, newState, idusuarioSet)
          )
        ) {
          return;
        }
        // El http-error.interceptor muestra el toast con el mensaje del backend.
      }
    );
  }

  updateStateWithRepeatedVoucher(
    id: number,
    newState: number,
    idusuarioSet: number
  ) {
    this._cwService
      .setState(id, newState, idusuarioSet, undefined, undefined, true)
      .subscribe(
        () => {
          this.alertService.showSuccessAlert(
            'Se cambió el estado!',
            'La solicitud cambió su estado.'
          );
          this.closeModal();
        },
        () => {
          // El http-error.interceptor muestra el toast con el mensaje del backend.
        }
      );
  }

  private handleRepeatedVoucherConfirmation(
    error: ViewPayErrorResponse,
    context: {
      id: number;
      newState: number;
      idusuarioSet: number;
    },
    onConfirm: () => void
  ): boolean {
    const errorCode = error?.error?.code || error?.error?.data?.code;

    if (errorCode !== 'REPEATED_VOUCHER_CONFIRMATION_REQUIRED') {
      return false;
    }

    const message = extractErrorMessage(
      error,
      'es',
      'Se detectó un comprobante repetido. ¿Deseas continuar de todos modos?',
    );

    const modalRef = this._dynamicModal.openModal(
      RepeatedVoucherActionModalComponent,
      'Comprobante repetido',
      'md',
      true,
      { message }
    );

    modalRef.result.then(
      (result: RepeatedVoucherModalResult) => {
        if (result?.action === 'continue') {
          onConfirm();
          return;
        }

        if (result?.action === 'reject') {
          this._cwService
            .setState(
              context.id,
              STATE_NAMES.REJECTED,
              context.idusuarioSet,
              DUPLICATE_VOUCHER_REJECT_REASON
            )
            .subscribe({
              next: () => {
                this.alertService.showSuccessAlert(
                  'Solicitud rechazada',
                  'La solicitud fue rechazada por comprobante repetido.'
                );
                this.closeModal();
              },
              error: (rejectError: ViewPayErrorResponse) => {
                this.alertService.showErrorAlert(
                  'Error al rechazar la solicitud',
                  rejectError?.error?.message ||
                    'No se pudo rechazar la solicitud.'
                );
              },
            });
        }
      },
      () => {}
    );

    return true;
  }

  // MODAL
  openEditCw(id: number, estado: number) {
    this._dynamicModal.openModal(
      EditCwRequestComponent,
      'Edición de Solicitud de Cargas o Retiros',
      'xl',
      false,
      { id, estado }
    );
  }
}
